import type { IndexLesson } from './path.ts';

/**
 * The island template every word set is drawn with. One silhouette, one trail, fixed spots for
 * props and stickers, in a 600 × 360 local box; the SVG viewBox leaves sea on both sides for the
 * boat route in and the pier out.
 */
export const ISLE = {
  w: 600,
  h: 360,
  /** viewBox: sea to the left for the route in, room on the right for the pier. */
  view: { x: -120, y: -50, w: 840, h: 470 },
  outline:
    'M46 180 C40 90 150 18 300 16 C450 14 560 50 586 180 C610 310 480 352 300 346 C120 340 52 270 46 180 Z',
  /** In from the sea on the left, out by the pier on the right. */
  trail:
    'M-90 200 C-20 200 40 150 110 120 C180 90 200 250 300 262 C400 274 400 100 490 104 C560 108 580 190 600 190 L720 190',
  /** Where the three props stand. */
  spots: [
    [250, 72],
    [400, 318],
    [500, 300],
  ] as const,
  /** One sticker pad per word, filled as the child finishes stops. */
  pads: [
    [318, 58],
    [364, 66],
    [408, 88],
    [102, 258],
    [140, 292],
    [452, 320],
  ] as const,
  sign: [150, 30] as const,
  /** Fractions of the trail's length: where the road starts, the seven land stops, the pier, the gate, the reward. */
  shore: 0.155,
  landFrom: 0.215,
  landTo: 0.815,
  pier: 0.9,
  gate: 0.915,
  reward: 0.985,
} as const;

export type Feature = 'none' | 'river' | 'sun' | 'road' | 'pond';

export interface Skin {
  ground: string;
  rim: string;
  feature: Feature;
}

/** Per-lesson skins for Primary 1. Anything not listed cycles through the palette below. */
const SKINS: Record<string, Skin> = {
  'p1a-l01': { ground: '#F1E4C8', rim: '#EBD6A9', feature: 'pond' },
  'p1a-l02': { ground: '#F6E3C4', rim: '#EFD5A6', feature: 'none' },
  'p1a-l03': { ground: '#D6EBC8', rim: '#E9DDB0', feature: 'river' },
  'p1a-l04': { ground: '#F5D9E4', rim: '#EFD0BE', feature: 'none' },
  'p1a-l05': { ground: '#E3EDD9', rim: '#EBDDB5', feature: 'sun' },
  'p1a-l06': { ground: '#CFE6D2', rim: '#E4D9A9', feature: 'river' },
  'p1a-l07': { ground: '#E4EFCB', rim: '#EEDDB0', feature: 'pond' },
  'p1a-l08': { ground: '#FBE0CC', rim: '#F0D3B0', feature: 'road' },
  'p1a-l09': { ground: '#FBEFC4', rim: '#EFDBA2', feature: 'sun' },
  'p1a-l10': { ground: '#DCE3EC', rim: '#E6DDC4', feature: 'pond' },
  'p1b-l11': { ground: '#E5DDF1', rim: '#EAD8C6', feature: 'road' },
  'p1b-l12': { ground: '#FADCD3', rim: '#F1D3B4', feature: 'none' },
  'p1b-l13': { ground: '#D3EFE8', rim: '#E5DEBB', feature: 'river' },
  'p1b-l14': { ground: '#E4DEF3', rim: '#EBD9C5', feature: 'none' },
  'p1b-l15': { ground: '#FBE3C6', rim: '#F0D5A8', feature: 'road' },
  'p1b-l16': { ground: '#EEE2CF', rim: '#E6D3B0', feature: 'none' },
  'p1b-l17': { ground: '#FBE7B0', rim: '#F2D68E', feature: 'sun' },
  'p1b-l18': { ground: '#EFE3BF', rim: '#E6D3A0', feature: 'road' },
  'p1b-l19': { ground: '#D4E9E4', rim: '#E4DBB8', feature: 'pond' },
};

const CYCLE: Skin[] = [
  { ground: '#F6E3C4', rim: '#EFD5A6', feature: 'none' },
  { ground: '#D6EBC8', rim: '#E9DDB0', feature: 'river' },
  { ground: '#E3EDD9', rim: '#EBDDB5', feature: 'sun' },
  { ground: '#F5D9E4', rim: '#EFD0BE', feature: 'pond' },
  { ground: '#EFE3BF', rim: '#E6D3A0', feature: 'road' },
  { ground: '#DCE3EC', rim: '#E6DDC4', feature: 'none' },
];

export function skinFor(lesson: IndexLesson): Skin {
  return SKINS[lesson.id] ?? CYCLE[(lesson.order - 1) % CYCLE.length]!;
}

export interface Pt {
  x: number;
  y: number;
}

export interface TrailGeometry {
  length: number;
  at: (len: number) => Pt;
}

/**
 * Measure the trail once with a detached path element, so stops, paw prints and dashes can be
 * placed by path length. Returns null outside a browser.
 */
export function measureTrail(): TrailGeometry | null {
  if (typeof document === 'undefined') return null;
  const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  p.setAttribute('d', ISLE.trail);
  let length: number;
  try {
    length = p.getTotalLength();
  } catch {
    return null;
  }
  if (!Number.isFinite(length) || length <= 0) return null;
  return {
    length,
    at: (len) => {
      const q = p.getPointAtLength(Math.max(0, Math.min(length, len)));
      return { x: q.x, y: q.y };
    },
  };
}

/** Points along a stretch of the trail as an SVG polyline `points` string. */
export function polyline(g: TrailGeometry, from: number, to: number, step = 6): string {
  const pts: string[] = [];
  for (let d = from; d <= to; d += step) {
    const p = g.at(d);
    pts.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`);
  }
  return pts.join(' ');
}

export interface Paw {
  x: number;
  y: number;
  angle: number;
}

/** Paw prints along a walked stretch, alternating left and right of the centre line. */
export function pawPrints(g: TrailGeometry, from: number, to: number): Paw[] {
  const out: Paw[] = [];
  let flip = 1;
  for (let d = from + 18; d < to - 12; d += 26) {
    const p = g.at(d);
    const q = g.at(d + 1);
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    const n = Math.hypot(dx, dy) || 1;
    out.push({
      x: p.x + (-dy / n) * 5.5 * flip,
      y: p.y + (dx / n) * 5.5 * flip,
      angle: (Math.atan2(dy, dx) * 180) / Math.PI + 90,
    });
    flip = -flip;
  }
  return out;
}

/** Trail fraction of the i-th of n land stops. */
export const landFraction = (i: number, n: number) =>
  ISLE.landFrom + ((ISLE.landTo - ISLE.landFrom) * i) / Math.max(1, n - 1);

/** Shizi's sprite size on an island, in island units. */
export const SHIZI_SIZE = 84;
/** Where a stop's caption sits (its name, or START on the current stop), relative to the stop. */
export const STOP_LABEL_DY = 56;

interface Box {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** How deep a square of side `size` centred on `p` cuts into `b` (0 when clear). */
function penetration(p: Pt, size: number, b: Box): number {
  const h = size / 2;
  const dx = Math.min(p.x + h, b.x2) - Math.max(p.x - h, b.x1);
  const dy = Math.min(p.y + h, b.y2) - Math.max(p.y - h, b.y1);
  return dx > 0 && dy > 0 ? Math.min(dx, dy) : 0;
}

/**
 * Where Shizi stands: beside the current stop, wherever he covers the least. Every other stop's
 * disc and every caption (the stop names, and START under the current stop) count, so the stop
 * the child just finished stays visible and tappable; so does the lesson sign in the top-left
 * corner, and the sea beyond the shore. `others` are the other stops; `stops` are all of them,
 * the current one included, for their captions. Returns his centre.
 */
export function shiziSpot(
  cur: Pt,
  others: Pt[],
  size = SHIZI_SIZE,
  stops: Pt[] = others,
  hasSign = true,
): Pt {
  const STOP_R = 26;
  const boxes: Box[] = [];
  for (const o of others)
    boxes.push({ x1: o.x - STOP_R, y1: o.y - STOP_R, x2: o.x + STOP_R, y2: o.y + STOP_R });
  for (const o of stops)
    boxes.push({
      x1: o.x - 50,
      y1: o.y + STOP_LABEL_DY - 10,
      x2: o.x + 50,
      y2: o.y + STOP_LABEL_DY + 8,
    });
  // START pill under the current stop, and the lesson sign on a lesson's first island.
  boxes.push({ x1: cur.x - 30, y1: cur.y + 62, x2: cur.x + 30, y2: cur.y + 82 });
  if (hasSign) boxes.push({ x1: -20, y1: -22, x2: 335, y2: 58 });
  // The island's ground, roughly, as an ellipse in the 600 × 360 box.
  const cx = 316;
  const cy = 181;
  const rx = 285;
  const ry = 170;
  let best: { p: Pt; cost: number } | null = null;
  for (const reach of [size * 0.95, size * 1.3, size * 1.65]) {
    for (let i = 0; i < 12; i++) {
      const a = (Math.PI * 2 * i) / 12;
      const p = { x: cur.x + Math.cos(a) * reach, y: cur.y + Math.sin(a) * reach };
      let cost = 0;
      for (const b of boxes) cost += penetration(p, size, b) * 2;
      const sea = Math.max(0, ((p.x - cx) / rx) ** 2 + ((p.y - cy) / ry) ** 2 - 1);
      cost += sea * size * 3;
      // Nearer and in front of (below) the stop when spots are otherwise equal.
      cost += (reach - size) * 0.05 + (Math.sin(a) > 0 ? 0 : 0.5);
      if (!best || cost < best.cost) best = { p, cost };
    }
  }
  return best!.p;
}
